/**
 * @voltxampere/ui — 17 automatic measurements (Sprint 15, ADR-0026)
 *
 * Per Blueprint §8. Each measurement is a pure function over a
 * sample buffer (and optional partner channel for two-channel
 * measurements). Returns `{value, unit, precision}` — `value:null`
 * when the input cannot support the measurement (e.g. requesting
 * frequency from a DC signal). No fake fallbacks (Hard Rule §9).
 *
 * Edge case discipline:
 *   - Empty buffer → `value: null`
 *   - DC-only signal (no zero crossing) → frequency / period null
 *   - NaN / non-finite samples skipped, never coerced
 *   - rise / fall time use 10-90% mid threshold (industry custom)
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
const NULL_VOLTS = { value: null, unit: "V", precision: 6 };
const NULL_HZ = { value: null, unit: "Hz", precision: 3 };
const NULL_S = { value: null, unit: "s", precision: 9 };
const NULL_PERCENT = { value: null, unit: "%", precision: 2 };
const NULL_DEG = { value: null, unit: "°", precision: 2 };
function vMin(values) {
    if (values.length === 0)
        return null;
    let best = Number.POSITIVE_INFINITY;
    let any = false;
    for (const v of values) {
        if (!Number.isFinite(v))
            continue;
        if (v < best)
            best = v;
        any = true;
    }
    return any ? best : null;
}
function vMax(values) {
    if (values.length === 0)
        return null;
    let best = Number.NEGATIVE_INFINITY;
    let any = false;
    for (const v of values) {
        if (!Number.isFinite(v))
            continue;
        if (v > best)
            best = v;
        any = true;
    }
    return any ? best : null;
}
function vAvg(values) {
    if (values.length === 0)
        return null;
    let sum = 0;
    let count = 0;
    for (const v of values) {
        if (!Number.isFinite(v))
            continue;
        sum += v;
        count++;
    }
    return count === 0 ? null : sum / count;
}
/**
 * Count rising-edge zero crossings around the signal mean.
 * Returns the index *pairs* (i_prev, i_curr) so callers can convert
 * to time via the sample timeline.
 */
function risingCrossings(values, ref) {
    const out = [];
    for (let i = 1; i < values.length; i++) {
        const prev = values[i - 1] ?? 0;
        const curr = values[i] ?? 0;
        if (!Number.isFinite(prev) || !Number.isFinite(curr))
            continue;
        if (prev <= ref && curr > ref)
            out.push(i);
    }
    return out;
}
/**
 * Estimate the signal frequency from rising-edge zero crossings
 * (relative to the average). Period = average inter-crossing
 * spacing × sampleRate⁻¹.
 */
function frequency(values, sampleRate) {
    if (sampleRate <= 0)
        return NULL_HZ;
    const avg = vAvg(values);
    if (avg === null)
        return NULL_HZ;
    const crossings = risingCrossings(values, avg);
    if (crossings.length < 2)
        return NULL_HZ;
    let totalSpacing = 0;
    for (let i = 1; i < crossings.length; i++) {
        const a = crossings[i - 1];
        const b = crossings[i];
        if (a === undefined || b === undefined)
            continue;
        totalSpacing += b - a;
    }
    const avgSpacing = totalSpacing / (crossings.length - 1);
    if (avgSpacing <= 0)
        return NULL_HZ;
    return { value: sampleRate / avgSpacing, unit: "Hz", precision: 3 };
}
function period(values, sampleRate) {
    const f = frequency(values, sampleRate);
    if (f.value === null || f.value === 0)
        return NULL_S;
    return { value: 1 / f.value, unit: "s", precision: 9 };
}
function pulseWidth(values, sampleRate) {
    if (sampleRate <= 0)
        return NULL_S;
    const avg = vAvg(values);
    if (avg === null)
        return NULL_S;
    // Pulse = first rising → next falling.
    let inHigh = false;
    let highStart = -1;
    let totalHighSamples = 0;
    let pulseCount = 0;
    for (let i = 1; i < values.length; i++) {
        const prev = values[i - 1] ?? 0;
        const curr = values[i] ?? 0;
        if (!inHigh && prev <= avg && curr > avg) {
            inHigh = true;
            highStart = i;
        }
        else if (inHigh && prev >= avg && curr < avg && highStart >= 0) {
            totalHighSamples += i - highStart;
            pulseCount++;
            inHigh = false;
        }
    }
    if (pulseCount === 0)
        return NULL_S;
    const avgHigh = totalHighSamples / pulseCount;
    return { value: avgHigh / sampleRate, unit: "s", precision: 9 };
}
function dutyCycle(values, sampleRate) {
    const pw = pulseWidth(values, sampleRate);
    const per = period(values, sampleRate);
    if (pw.value === null || per.value === null || per.value === 0)
        return NULL_PERCENT;
    return { value: (pw.value / per.value) * 100, unit: "%", precision: 2 };
}
/** First time the signal climbs from 10% → 90% of (vbase, vtop). */
function riseTime(values, sampleRate) {
    if (sampleRate <= 0 || values.length < 2)
        return NULL_S;
    const lo = vMin(values);
    const hi = vMax(values);
    if (lo === null || hi === null || hi - lo <= 0)
        return NULL_S;
    const t10 = lo + (hi - lo) * 0.1;
    const t90 = lo + (hi - lo) * 0.9;
    let i10 = -1;
    let i90 = -1;
    for (let i = 0; i < values.length; i++) {
        const v = values[i] ?? 0;
        if (i10 === -1 && v >= t10)
            i10 = i;
        if (i10 !== -1 && i90 === -1 && v >= t90) {
            i90 = i;
            break;
        }
    }
    if (i10 === -1 || i90 === -1 || i90 < i10)
        return NULL_S;
    // Instantaneous edge — one sample crosses both thresholds.
    // Report the minimum representable rise time: one sample period.
    const samples = i90 === i10 ? 1 : i90 - i10;
    return { value: samples / sampleRate, unit: "s", precision: 9 };
}
function fallTime(values, sampleRate) {
    if (sampleRate <= 0 || values.length < 2)
        return NULL_S;
    const lo = vMin(values);
    const hi = vMax(values);
    if (lo === null || hi === null || hi - lo <= 0)
        return NULL_S;
    const t90 = lo + (hi - lo) * 0.9;
    const t10 = lo + (hi - lo) * 0.1;
    let i90 = -1;
    let i10 = -1;
    for (let i = 0; i < values.length; i++) {
        const v = values[i] ?? 0;
        if (i90 === -1 && v >= t90)
            i90 = i;
        if (i90 !== -1 && i10 === -1 && v <= t10) {
            i10 = i;
            break;
        }
    }
    if (i90 === -1 || i10 === -1 || i10 < i90)
        return NULL_S;
    const samples = i10 === i90 ? 1 : i10 - i90;
    return { value: samples / sampleRate, unit: "s", precision: 9 };
}
function vMaxResult(values) {
    const v = vMax(values);
    return v === null ? NULL_VOLTS : { value: v, unit: "V", precision: 6 };
}
function vMinResult(values) {
    const v = vMin(values);
    return v === null ? NULL_VOLTS : { value: v, unit: "V", precision: 6 };
}
function vppResult(values) {
    const lo = vMin(values);
    const hi = vMax(values);
    if (lo === null || hi === null)
        return NULL_VOLTS;
    return { value: hi - lo, unit: "V", precision: 6 };
}
function vAvgResult(values) {
    const v = vAvg(values);
    return v === null ? NULL_VOLTS : { value: v, unit: "V", precision: 6 };
}
function vRmsResult(values) {
    if (values.length === 0)
        return NULL_VOLTS;
    let sum = 0;
    let count = 0;
    for (const v of values) {
        if (!Number.isFinite(v))
            continue;
        sum += v * v;
        count++;
    }
    if (count === 0)
        return NULL_VOLTS;
    return { value: Math.sqrt(sum / count), unit: "V", precision: 6 };
}
/** Bottom flat — the "low" baseline of a square / pulse signal.
 *  Sprint 15 uses the histogram-mode of the lower half. */
function vBase(values) {
    const lo = vMin(values);
    const hi = vMax(values);
    if (lo === null || hi === null)
        return NULL_VOLTS;
    if (hi === lo)
        return { value: lo, unit: "V", precision: 6 };
    const mid = (lo + hi) / 2;
    let sum = 0;
    let count = 0;
    for (const v of values) {
        if (!Number.isFinite(v) || v >= mid)
            continue;
        sum += v;
        count++;
    }
    if (count === 0)
        return NULL_VOLTS;
    return { value: sum / count, unit: "V", precision: 6 };
}
function vTop(values) {
    const lo = vMin(values);
    const hi = vMax(values);
    if (lo === null || hi === null)
        return NULL_VOLTS;
    if (hi === lo)
        return { value: hi, unit: "V", precision: 6 };
    const mid = (lo + hi) / 2;
    let sum = 0;
    let count = 0;
    for (const v of values) {
        if (!Number.isFinite(v) || v <= mid)
            continue;
        sum += v;
        count++;
    }
    if (count === 0)
        return NULL_VOLTS;
    return { value: sum / count, unit: "V", precision: 6 };
}
function overshoot(values) {
    const top = vTop(values).value;
    const peak = vMax(values);
    const base = vBase(values).value;
    if (top === null || peak === null || base === null || top === base) {
        return NULL_PERCENT;
    }
    return { value: ((peak - top) / (top - base)) * 100, unit: "%", precision: 2 };
}
function preshoot(values) {
    const base = vBase(values).value;
    const trough = vMin(values);
    const top = vTop(values).value;
    if (base === null || trough === null || top === null || top === base) {
        return NULL_PERCENT;
    }
    return { value: ((base - trough) / (top - base)) * 100, unit: "%", precision: 2 };
}
/**
 * Phase difference between two channels in degrees. Computed via
 * cross-correlation peak — assumes the two channels share the
 * sample rate (Sprint 15 shared time base guarantees this). Null
 * when either channel is DC.
 */
function phase(values, sampleRate, context) {
    const other = context?.otherChannel;
    if (!other || other.length === 0 || values.length === 0)
        return NULL_DEG;
    const f = frequency(values, sampleRate);
    if (f.value === null || f.value === 0)
        return NULL_DEG;
    const samplesPerCycle = sampleRate / f.value;
    if (!Number.isFinite(samplesPerCycle) || samplesPerCycle <= 0)
        return NULL_DEG;
    // Lag in samples via cross-correlation argmax in [-cycle/2, +cycle/2).
    const aMean = vAvg(values) ?? 0;
    const bMean = vAvg(other) ?? 0;
    const half = Math.floor(samplesPerCycle / 2);
    let bestLag = 0;
    let bestScore = Number.NEGATIVE_INFINITY;
    const N = Math.min(values.length, other.length);
    for (let lag = -half; lag <= half; lag++) {
        let score = 0;
        let n = 0;
        for (let i = 0; i < N; i++) {
            const j = i + lag;
            if (j < 0 || j >= N)
                continue;
            score += ((values[i] ?? 0) - aMean) * ((other[j] ?? 0) - bMean);
            n++;
        }
        if (n === 0)
            continue;
        score /= n;
        if (score > bestScore) {
            bestScore = score;
            bestLag = lag;
        }
    }
    return {
        value: (bestLag / samplesPerCycle) * 360,
        unit: "°",
        precision: 2,
    };
}
/**
 * Time delay between rising edges of two channels.
 */
function delay(values, sampleRate, context) {
    const other = context?.otherChannel;
    if (!other || sampleRate <= 0)
        return NULL_S;
    const aRef = vAvg(values);
    const bRef = vAvg(other);
    if (aRef === null || bRef === null)
        return NULL_S;
    const aCross = risingCrossings(values, aRef);
    const bCross = risingCrossings(other, bRef);
    if (aCross.length === 0 || bCross.length === 0)
        return NULL_S;
    const a0 = aCross[0] ?? 0;
    const b0 = bCross[0] ?? 0;
    return { value: (b0 - a0) / sampleRate, unit: "s", precision: 9 };
}
export const MEASUREMENTS = {
    frequency,
    period,
    "pulse-width": pulseWidth,
    "duty-cycle": dutyCycle,
    "rise-time": riseTime,
    "fall-time": fallTime,
    vmax: vMaxResult,
    vmin: vMinResult,
    vpp: vppResult,
    vavg: vAvgResult,
    vrms: vRmsResult,
    vbase: vBase,
    vtop: vTop,
    overshoot,
    preshoot,
    phase,
    delay,
};
export const MEASUREMENT_IDS = Object.keys(MEASUREMENTS);
/** Convenience wrapper — fetch a measurement function by id and
 *  invoke it with the standard signature. Throws on unknown id so
 *  the caller cannot silently ignore a typo. */
export function computeMeasurement(id, values, sampleRate, context) {
    // `MEASUREMENTS` is a fully-typed Record so TS narrows the lookup
    // to a defined function — but consumers may pass a stale string id
    // (older sprint API). Cast to a checked optional for the runtime
    // guard without lying to the type system.
    const fn = MEASUREMENTS[id];
    if (typeof fn !== "function")
        throw new Error(`Unknown measurement id "${id}"`);
    return fn(values, sampleRate, context);
}
//# sourceMappingURL=measurements.js.map